import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import i18n, { SUPPORTED_LANGS } from "@/i18n";

/**
 * Reads the `:lang` URL prefix (when present) and forces i18n to that language.
 * Also maintains `<link rel="alternate" hreflang="…">` tags in <head> for the
 * current path so Google can index French and Portuguese variants.
 */
const codes = SUPPORTED_LANGS.map((l) => l.code); // ['en','fr','pt']

function parseLangPrefix(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length && codes.includes(parts[0])) {
    return { lang: parts[0], rest: "/" + parts.slice(1).join("/") };
  }
  return { lang: null, rest: pathname || "/" };
}

function stripLangFromPath(pathname) {
  return parseLangPrefix(pathname).rest;
}

function upsertAlternate(lang, href) {
  const selector = `link[rel="alternate"][hreflang="${lang}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", lang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function LangSync() {
  const location = useLocation();

  // Sync i18n with URL lang prefix. When the URL has no prefix we force English
  // so the canonical (bare) tree always serves the English content implied by
  // our hreflang alternates.
  useEffect(() => {
    const { lang } = parseLangPrefix(location.pathname);
    const target = lang || "en";
    if (i18n.language !== target) {
      i18n.changeLanguage(target);
    }
  }, [location.pathname]);

  // Maintain hreflang alternates for the current page across all locales
  useEffect(() => {
    if (typeof document === "undefined") return;
    const origin = window.location.origin;
    const basePath = stripLangFromPath(location.pathname);
    const paths = {
      en: basePath === "/" ? "/" : basePath,
      fr: basePath === "/" ? "/fr" : `/fr${basePath}`,
      pt: basePath === "/" ? "/pt" : `/pt${basePath}`,
    };
    for (const code of codes) {
      upsertAlternate(code, `${origin}${paths[code]}`);
    }
    upsertAlternate("x-default", `${origin}${paths.en}`);
  }, [location.pathname]);

  return null;
}

export { stripLangFromPath, parseLangPrefix, codes as SUPPORTED_CODES };
